



































import { computed, defineComponent, ref, watch } from "@vue/composition-api";
import { ContentSet } from "@scrile/api-provider/dist/api/ContentSetProvider";
import { TagInfoType } from "@scrile/api-provider/dist/api/TagsProvider";
import { ContentFeedTypeEnum } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAuthUser from "@/hooks/useAuthUser";
import useUserTags from "@/hooks/useUserTags";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import config from "@/config";
import { EVENTS } from "@/const";
import ChoosePostTypeController from "@/components/ChoosePostTypeController";
import GoingLiveConfirm from "@/components/GoingLiveConfirm";
import SelectUserTagsModal from "@/components/SelectUserTagsModal";
import CreatePostButtonView from "./components/CreatePostButtonView";

export default defineComponent({
  name: "CreatePostButtonController",
  components: { GoingLiveConfirm, ChoosePostTypeController, SelectUserTagsModal, CreatePostButtonView },
  props: {
    userId: { type: String, required: true },
    type: { type: String as () => ContentFeedTypeEnum | null },
    onlyLives: { type: Boolean, default: false },
  },
  setup(props) {
    const { authUser, authorities, isFullCreator } = useAuthUser();
    const { userTags, userTagsGetData } = useUserTags();
    const loading = ref(false);
    const showSelectTags = ref(false);
    const showChooseType = ref(false);
    const postType = ref<ContentFeedTypeEnum | undefined | null>(props.type);
    const draft = ref<ContentSet | null>(null);
    const showLiveConfirm = ref(false);
    const onlyLivesAllowed = ref(props.onlyLives);

    const isAuthor = computed(() => authUser.value && authUser.value.id === props.userId);
    const requiredAuthorities = computed(() => (isAuthor.value ? ["createMyContentSet"] : ["createAnyContentSet"]));
    const allowCreate = computed(() => checkAuthorities(authorities.value, requiredAuthorities.value));

    const onSetPostType = (type: ContentFeedTypeEnum) => {
      postType.value = type;
    };

    const onSetPostData = (data: ContentSet, cb?: () => void) => {
      draft.value = data;
      cb && cb();
    };

    const createPost = async (type: ContentFeedTypeEnum, userId: string) => {
      draft.value = await providers.ContentSetProvider.create({ data: { type, userId } });
    };

    const onCreatePost = async (type: ContentFeedTypeEnum, cb?: () => void) => {
      try {
        loading.value = true;
        await createPost(type, props.userId);
      } finally {
        loading.value = false;
        cb && cb();
      }
    };

    const getTagsData = async () => {
      if (config.featureTags) {
        try {
          loading.value = true;
          await userTagsGetData();
        } finally {
          loading.value = false;
        }
      }
    };

    const onCloseModal = () => {
      draft.value = null;
      postType.value = props.type;
      showChooseType.value = false;
      onlyLivesAllowed.value = props.onlyLives;
    };

    const onShowLiveConfirm = () => {
      showLiveConfirm.value = true;
      onCloseModal();
    };

    const onSaveTags = (value: string[]) => {
      if (value.length > 0) {
        userTags.value = value;
        showSelectTags.value = false;
        showChooseType.value = true;
      }
    };

    const onClick = async () => {
      emitter.emit(EVENTS.CHOOSE_POST_TYPE, () => {
        onlyLivesAllowed.value = true;
      });
      await getTagsData();
      if (config.featureTags && isFullCreator.value && userTags.value.length === 0) {
        showSelectTags.value = true;
      } else {
        showChooseType.value = true;
      }
    };

    watch(draft, value => {
      if (value) {
        emitter.emit(EVENTS.SHOW_CREATE_POST, { postId: value.id, userId: props.userId, type: postType.value });
        onCloseModal();
      }
    });

    return {
      loading,
      postType,
      allowCreate,
      showChooseType,
      showSelectTags,
      showLiveConfirm,
      userTags,
      onlyLivesAllowed,
      onClick,
      onSetPostType,
      onSetPostData,
      onCreatePost,
      onCloseModal,
      onShowLiveConfirm,
      onSaveTags,
      TagInfoType,
    };
  },
});
